import { useCallback, useEffect, useMemo, useState } from "react";
import * as ContextMenu from "@radix-ui/react-context-menu";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useResolveAdContent } from "@/shared/hooks/useResolveAdContent";
import { Ad, resolvePreviewMedia, type ImageContent, type SMSContent, type VideoContent } from "@models/Ad.model";
import type { ResolvedAdContent } from "@services/media/commands/cmdResolveAds";
import { AdCardHeader } from "./AdCardHeader";
import AdCardFullModal from "./AdCardFullModal";
import CoverPicker from "@components/CoverPicker/CoverPicker";
import AdCardMedia from "./renderers/AdCardMedia";
import AdCardEmail from "./renderers/AdCardEmail";
import AdCardSms from "./renderers/AdCardSms";
import AdCardDocument from "./renderers/AdCardDocument";
import AdCardDirectMail from "./renderers/AdCardDirectMail";
import type { AdCardMenuAction, AdCardProps, AdCardRendererProps } from "./types";
import "./AdCard.scss";

function LoadingPlaceholder({ mode }: AdCardRendererProps) {
    return (
        <>
            <div className="adcard__loading" />
            {mode !== "compact" && (
                <>
                    <div className="adcard-media__copy adcard-media__copy--skeleton">
                        <div className="adcard__skeleton-line" />
                        <div className="adcard__skeleton-line adcard__skeleton-line--short" />
                    </div>
                    <div className="adcard-media__footer adcard-media__footer--skeleton">
                        <div className="adcard-media__footer-text">
                            <div className="adcard__skeleton-line" />
                            <div className="adcard__skeleton-line adcard__skeleton-line--short" />
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

function EmptyPlaceholder({ mode }: AdCardRendererProps) {
    return (
        <div className={`adcard__empty${mode === "compact" ? " adcard__empty--compact" : ""}`}>
            <span className="adcard__empty-title">Nothing in this ad yet</span>
            {mode !== "compact" && (
                <span className="adcard__empty-hint">Open it to add content</span>
            )}
        </div>
    );
}

function RendererStub({ ad, mode }: AdCardRendererProps) {
    return (
        <div className="adcard__stub">
            [{ad.type} renderer not implemented — mode: {mode}]
        </div>
    );
}

function pickRenderer(type: string | undefined): (props: AdCardRendererProps) => JSX.Element {
    if (type === "image" || type === "video") return AdCardMedia;
    if (type === "email") return AdCardEmail;
    if (type === "sms") return AdCardSms;
    if (type === "document") return AdCardDocument;
    if (type === "direct_mail") return AdCardDirectMail;
    if (type === "container" || type === undefined) return LoadingPlaceholder;
    return RendererStub;
}

const DEFAULT_ACTIONS: AdCardMenuAction[] = [
    { label: "Edit Ad", onSelect: () => {} },
    { label: "Rename Ad", onSelect: () => {} },
    { label: "Delete Ad", onSelect: () => {}, danger: true },
];

function buildInlineResolved(ad: Ad): ResolvedAdContent | undefined {
    if (ad.type === "container" || !ad.content) return undefined;
    const c = ad.content as VideoContent | ImageContent | SMSContent;
    const media = ("video" in c && c.video) || ("image" in c && c.image) || null;
    const preview = resolvePreviewMedia(c);
    if (!media && !preview) return undefined;
    return { ad, media, preview };
}

export default function AdCard({ ad, mode, dnd, menuActions, isSelected, onClick, hideType, hideMenu, contextMenu, sourceTemplateName, contentLoaded }: AdCardProps) {
    const adId = typeof ad === "number" ? ad : ad.id;
    const inlineResolved = typeof ad === "object" ? buildInlineResolved(ad) : undefined;
    const [resolveRef, fetchedResolved] = useResolveAdContent(adId, !!inlineResolved || contentLoaded !== undefined);
    const resolved = inlineResolved ?? fetchedResolved;
    const [fullOpen, setFullOpen] = useState(false);
    const [pickingCover, setPickingCover] = useState(false);
    // Shown at once after a save, so the card does not refetch the catalog for one image.
    const [pickedCover, setPickedCover] = useState<string | null>(null);
    useEffect(() => () => { if (pickedCover) URL.revokeObjectURL(pickedCover); }, [pickedCover]);

    const {
        attributes, listeners,
        setNodeRef: setDragRef,
        transform, isDragging,
    } = useDraggable({
        id: dnd?.dragId ?? `adcard-${adId}`,
        data: dnd?.dragData,
        disabled: !dnd,
    });

    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: dnd?.dropId ?? `adcard-drop-${adId}`,
        data: dnd?.dropData,
        disabled: !dnd,
    });

    const setNodeRef = useCallback((element: HTMLElement | null) => {
        setDragRef(element);
        setDropRef(element);
        resolveRef(element);
    }, [setDragRef, setDropRef, resolveRef]);

    const resolvedMenu = useMemo<AdCardMenuAction[]>(() => {
        const base = menuActions ?? DEFAULT_ACTIONS;
        const hasViewFull = base.some((a) => a.label === "View full");
        if (hasViewFull) return base;
        return [...base, { label: "View full", onSelect: () => setFullOpen(true) }];
    }, [menuActions]);

    const style = transform
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
        : undefined;

    const providedAd = typeof ad === "object" ? ad : undefined;
    const headerAd = providedAd ?? resolved?.ad ?? new Ad({ id: adId });
    const renderAd = resolved?.ad ?? providedAd ?? new Ad({ id: adId });
    const dispatchType =
        resolved?.ad.type
        ?? (providedAd?.type === "container" ? providedAd.subtype : undefined)
        ?? providedAd?.type;

    const classes = [
        "adcard",
        `adcard--${mode}`,
        `adcard--type-${dispatchType}`,
        isSelected ? "adcard--selected" : "",
        dnd ? "adcard--draggable" : "",
        isDragging ? "adcard--dragging" : "",
        isOver ? "adcard--drop-over" : "",
    ].filter(Boolean).join(" ");

    // `null` is the resolve endpoint saying this ad has no content to show, which is
    // not the same as `undefined` (not resolved yet). Without the distinction an empty
    // container shows the loading skeleton forever and reads as a broken card.
    const isEmpty = !inlineResolved && (fetchedResolved === null || contentLoaded === true);
    const Renderer = isEmpty ? EmptyPlaceholder : pickRenderer(dispatchType);
    const rendererResolved = resolved ?? undefined;

    const card = (
        <div
            ref={setNodeRef}
            style={style}
            className={classes}
            onClick={onClick}
            {...(dnd ? attributes : {})}
            {...(dnd ? listeners : {})}
        >
            <AdCardHeader
                ad={headerAd}
                menuActions={resolvedMenu}
                hideType={hideType}
                hideMenu={hideMenu}
                sourceTemplateName={sourceTemplateName}
            />
            <div className="adcard__body">
                <Renderer ad={renderAd} mode={mode} resolved={rendererResolved} onOpenFull={() => setFullOpen(true)} onPickCover={() => setPickingCover(true)} pickedCover={pickedCover} />
            </div>
        </div>
    );

    return (
        <>
            {contextMenu ? (
                <ContextMenu.Root>
                    <ContextMenu.Trigger asChild>{card}</ContextMenu.Trigger>
                    <ContextMenu.Portal>
                        <ContextMenu.Content className="radix-context-menu-content" style={{ zIndex: 2000 }}>
                            {resolvedMenu.map((action) => (
                                <ContextMenu.Item
                                    key={action.label}
                                    className={[
                                        "radix-context-menu-item",
                                        action.danger ? "radix-context-menu-item--danger" : "",
                                    ].join(" ").trim()}
                                    onSelect={action.onSelect}
                                    disabled={action.disabled}
                                >
                                    {action.label}
                                </ContextMenu.Item>
                            ))}
                        </ContextMenu.Content>
                    </ContextMenu.Portal>
                </ContextMenu.Root>
            ) : (
                card
            )}
            <AdCardFullModal
                ad={renderAd}
                open={fullOpen}
                onClose={() => setFullOpen(false)}
                Renderer={Renderer}
                resolved={rendererResolved}
            />
            {pickingCover && rendererResolved?.media?.storage_uri && (
                <CoverPicker
                    adId={renderAd.id}
                    videoUrl={rendererResolved.media.storage_uri}
                    adName={renderAd.name}
                    onSaved={(previewUrl) => {
                        // Each save pins a full-resolution JPEG until revoked.
                        setPickedCover((prev) => { if (prev) URL.revokeObjectURL(prev); return previewUrl; });
                        setPickingCover(false);
                    }}
                    onClose={() => setPickingCover(false)}
                />
            )}
        </>
    );
}
